// URL-query-param channel for the `--viewer-*` theming tokens.
//
// Params are validated against an explicit allowlist of token names and
// value shapes before being applied — an unrecognized param, a malformed
// color, or an oversized value is silently dropped and the defaults hold.
// Values are applied via `CSSStyleDeclaration.setProperty`, which treats the
// value as a single declaration and rejects anything unparsable, so this
// channel cannot inject arbitrary CSS.

const MAX_VALUE_LENGTH = 128;

/** Validate a candidate value by round-tripping it through CSSOM parsing. */
function parsesAsStyle(
  property: "color" | "fontFamily",
  value: string,
): boolean {
  const probe = document.createElement("span");
  probe.style[property] = value;
  return probe.style[property] !== "";
}

const isColor = (value: string) => parsesAsStyle("color", value);
const isFontFamily = (value: string) => parsesAsStyle("fontFamily", value);

/** Allowlist: query param -> { token it writes, value validator }. */
const THEME_PARAMS: Record<
  string,
  { token: string; isValid: (value: string) => boolean }
> = {
  font: { token: "--viewer-font-family", isValid: isFontFamily },
  bg: { token: "--viewer-bg", isValid: isColor },
  fg: { token: "--viewer-fg", isValid: isColor },
  accent: { token: "--viewer-accent", isValid: isColor },
  muted: { token: "--viewer-muted", isValid: isColor },
  border: { token: "--viewer-border", isValid: isColor },
};

const MODES = ["light", "dark"] as const;

/**
 * Apply validated theme params from a query string onto `root` (the `<html>`
 * element in production — called before first paint so there is no flash of
 * the default theme). Returns the token names that were applied, for tests
 * and debugging.
 */
export function applyThemeParams(
  search: string,
  root: HTMLElement = document.documentElement,
): string[] {
  const params = new URLSearchParams(search);
  const applied: string[] = [];

  const mode = params.get("mode");
  if (mode && (MODES as readonly string[]).includes(mode)) {
    root.dataset.theme = mode;
    applied.push("mode");
  }

  for (const [name, { token, isValid }] of Object.entries(THEME_PARAMS)) {
    const value = params.get(name)?.trim();
    if (!value || value.length > MAX_VALUE_LENGTH || !isValid(value)) continue;
    root.style.setProperty(token, value);
    applied.push(token);
  }

  return applied;
}
